import React, { ReactNode, useEffect } from 'react';
import { Layout, Menu, theme, Button, App as AntdApp, Popconfirm } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  BookOutlined,
  UserOutlined,
  OrderedListOutlined,
  GlobalOutlined,
  FileTextOutlined,
  LogoutOutlined,
  TeamOutlined,
  SafetyOutlined,
  EyeOutlined,
  ExperimentOutlined,
  ToolOutlined,
  AuditOutlined,
  HeartOutlined,
  PlaySquareOutlined,
  SignatureOutlined,
  ShoppingOutlined,
  ClearOutlined,
  EnvironmentOutlined,
  ClusterOutlined,
  ContactsOutlined,
  ShareAltOutlined,
  SolutionOutlined,
} from '@ant-design/icons';
import { getCookie, setCookie } from '../utils/cookies';
import { API_URL } from '../config/apiUrls';

const { Header, Content, Sider } = Layout;

interface MainLayoutProps {
  children: ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { message } = AntdApp.useApp();
  const [collapsed, setCollapsed] = React.useState(() => {
    const saved = getCookie('sidebar-collapsed');
    return saved === 'true';
  });
  const [isAuthenticated, setIsAuthenticated] = React.useState(
    () => Boolean(localStorage.getItem('token')),
  );
  const [username, setUsername] = React.useState<string>('');
  const [clearingCache, setClearingCache] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleClearCache = async () => {
    setClearingCache(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/cache/reset`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        let errorMessage = `Failed to clear cache (${response.status})`;
        try {
          const data = await response.json();
          if (Array.isArray(data?.message)) {
            errorMessage = data.message.join(', ');
          } else if (typeof data?.message === 'string') {
            errorMessage = data.message;
          }
        } catch {
          // ignore JSON parse errors
        }
        message.error(errorMessage);
        return;
      }

      message.success('Cache cleared successfully');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to clear cache';
      message.error(errorMessage);
    } finally {
      setClearingCache(false);
    }
  };

  useEffect(() => {
    setCookie('sidebar-collapsed', collapsed.toString());
  }, [collapsed]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsAuthenticated(Boolean(token));
    if (!token) {
      setUsername('');
      return;
    }
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setUsername(payload.username || 'User');
    } catch {
      setUsername('User');
    }
  }, [location.pathname]);
  
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 16px',
        }}
      >
        <h1 style={{ color: 'white', margin: 0 }}>Grantha Management System</h1>
        {isAuthenticated ? (
          <Link to="/profile" style={{ color: 'white' }}>
            {username}
          </Link>
        ) : (
          <Link to="/login" style={{ color: 'white' }}>
            Log in
          </Link>
        )}
      </Header>
      <Layout>
        {isAuthenticated && (
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={(value) => setCollapsed(value)}
          width={200}
          style={{
            position: 'sticky',
            top: 0,
            alignSelf: 'flex-start',
            height: '100vh',
            overflow: 'hidden',
            zIndex: 10,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              paddingBottom: 48,
            }}
          >
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              <Menu
                theme="dark"
                mode="inline"
                selectedKeys={[
                  location.pathname.startsWith('/profile/saved')
                    ? '/profile/saved'
                    : location.pathname.startsWith('/purohits')
                      ? '/purohits'
                      : location.pathname,
                ]}
                style={{ borderInlineEnd: 'none' }}
                items={[
              {
                key: '/',
                icon: <BookOutlined />,
                label: <Link to="/">Grantha</Link>,
              },
              {
                key: '/contributors',
                icon: <ContactsOutlined />,
                label: <Link to="/contributors">Contributors</Link>,
              },
              {
                key: '/languages',
                icon: <GlobalOutlined />,
                label: <Link to="/languages">Language</Link>,
              },
              {
                key: '/countries',
                icon: <EnvironmentOutlined />,
                label: <Link to="/countries">Country</Link>,
              },
              {
                key: '/hierarchy',
                icon: <OrderedListOutlined />,
                label: <Link to="/hierarchy">Hierarchy</Link>,
              },
              {
                key: '/vyakhyanuvad',
                icon: <FileTextOutlined />,
                label: <Link to="/vyakhyanuvad">Vyakhyanuvad</Link>,
              },
              {
                key: '/users',
                icon: <TeamOutlined />,
                label: <Link to="/users">User Management</Link>,
              },
              {
                key: '/profile',
                icon: <UserOutlined />,
                label: <Link to="/profile">My Profile</Link>,
              },
              {
                key: '/profile/saved',
                icon: <ContactsOutlined />,
                label: <Link to="/profile/saved">Saved Profiles</Link>,
              },
              {
                key: '/purohits',
                icon: <SolutionOutlined />,
                label: <Link to="/purohits">Purohits</Link>,
              },
              {
                key: '/policies',
                icon: <SafetyOutlined />,
                label: <Link to="/policies">Policies</Link>,
              },
              {
                key: '/vyakhyanuvad-viewer',
                icon: <EyeOutlined />,
                label: <Link to="/vyakhyanuvad-viewer">Vyakhyanuvad Viewer</Link>,
              },
              {
                key: '/grantha-viewer-new',
                icon: <BookOutlined />,
                label: <Link to="/grantha-viewer-new">Grantha Viewer New</Link>,
              },
              {
                key: '/anukramanika',
                icon: <OrderedListOutlined />,
                label: <Link to="/anukramanika">Anukramanika</Link>,
              },
              {
                key: '/bidhi',
                icon: <ToolOutlined />,
                label: <Link to="/bidhi">Bidhi</Link>,
              },
              {
                key: '/karmakanda',
                icon: <PlaySquareOutlined />,
                label: <Link to="/karmakanda">Karmakanda</Link>,
              },
              {
                key: '/sankalpa',
                icon: <SignatureOutlined />,
                label: <Link to="/sankalpa">Sankalpa</Link>,
              },
              {
                key: '/puja',
                icon: <AuditOutlined />,
                label: <Link to="/puja">Puja</Link>,
              },
              {
                key: '/samagri',
                icon: <ShoppingOutlined />,
                label: <Link to="/samagri">Samagri</Link>,
              },
              {
                key: '/gotra',
                icon: <ClusterOutlined />,
                label: <Link to="/gotra">Gotra</Link>,
              },
              {
                key: '/vedsakhas',
                icon: <BookOutlined />,
                label: <Link to="/vedsakhas">Vedsakha</Link>,
              },
              {
                key: '/relations',
                icon: <ShareAltOutlined />,
                label: <Link to="/relations">Relations</Link>,
              },
              // {
              //   key: '/yjstest',
              //   icon: <ExperimentOutlined />,
              //   label: <Link to="/yjstest">YJS Test</Link>,
              // },
                ]}
              />
            </div>
            <div
              style={{
                flexShrink: 0,
                padding: '8px 16px',
                borderTop: '1px solid #434343',
                backgroundColor: '#001529',
              }}
            >
              <Popconfirm
                title="Clear cache?"
                description="This will reset the server cache. Continue?"
                onConfirm={handleClearCache}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  type="text"
                  icon={<ClearOutlined />}
                  loading={clearingCache}
                  style={{ color: 'white', width: '100%', marginBottom: '8px' }}
                  block
                >
                  {!collapsed && 'Clear Cache'}
                </Button>
              </Popconfirm>
              <Button
                type="text"
                icon={<LogoutOutlined />}
                onClick={handleLogout}
                style={{ color: 'white', width: '100%' }}
                block
              >
                {!collapsed && 'Logout'}
              </Button>
            </div>
          </div>
        </Sider>
        )}
        <Layout>
          <Content
            style={{
              margin: 0,
              background: colorBgContainer,
              borderRadius: borderRadiusLG,
              minHeight: 280,
            }}
          >
            {children}
          </Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default MainLayout;